import React, { useState, useMemo } from 'react'
import { PillTabs } from '../../FbUI/PillTabs'
import { TableLinkStyleProvider } from '../../Tables/CommonTableCells/TableLinkStyleContext'
import { UnmatchedDoorsTable } from '../../DataExpansionComponents/UnmatchedDoors/UnmatchedDoorsTable'
import { TabbedDoorSectionProps } from './types'
import { IContactCompany } from 'models/contact_companies'
import { FilterIdentifier } from 'models/saved_view'
import { formatInteger } from '../../../utils/formatting'
import { StandardSummaryResponse } from 'models/paginated-response'
import { PlacesSummary } from 'models/summaries'

interface DoorsTableTabsProps {
  forChainProxyId?: number
  chainID?: number
  chainData?: TabbedDoorSectionProps['chainData']
  subCategory: TabbedDoorSectionProps['subCategory']
  matchedTableComponent: TabbedDoorSectionProps['matchedTableComponent']
  matchedTableProps?: TabbedDoorSectionProps['matchedTableProps']
  matchedTableKey?: string
  unmatchedTableKeyPrefix: string
  className?: string
  /**
   * FIR-7760: the matched table's ACTUAL row count, derived once in DoorsModal
   * from the summary the matched table reports via `setMatchedSummary`. Drives
   * the "First Bite" tab badge so it matches the rows the table renders (and
   * the modal header, which reads the same value). Previously the badge read
   * the denormalized `chainData.count` — the chain's canonical size, which can
   * drift from the filtered rows (e.g. closed/invalid locations). When absent
   * (matched tables that don't report a summary, e.g. non-restaurant
   * verticals) the badge falls back to `chainData.count`.
   */
  matchedDoorCount?: number
  setMatchedSummary?: React.Dispatch<React.SetStateAction<PlacesSummary>>
}

export function DoorsTableTabs({
  forChainProxyId,
  chainID,
  chainData,
  subCategory,
  matchedTableComponent: MatchedTableComponent,
  matchedTableProps = {},
  matchedTableKey,
  unmatchedTableKeyPrefix,
  className = '',
  matchedDoorCount,
  setMatchedSummary,
}: DoorsTableTabsProps) {
  const [selectedTab, setSelectedTab] = useState<'matched' | 'unmatched'>(
    'matched'
  )
  const [unmatchedCount, setUnmatchedCount] = useState(0)

  // Create chainProxy object for UnmatchedDoorsTable
  const chainProxy: IContactCompany | undefined = useMemo(() => {
    if (!chainData || !subCategory) return undefined

    return {
      id: forChainProxyId || chainData.contact_company_id || 0,
      domain: chainData.domain || '',
      name: chainData.name || subCategory.name,
      sub_category: subCategory,
    } as IContactCompany
  }, [chainData, subCategory, forChainProxyId])

  const handleUnmatchedCountChange = (summary: StandardSummaryResponse) => {
    setUnmatchedCount(summary.alt_count || 0)
  }

  // FIR-7760: the "First Bite" badge reflects the doors the matched table
  // actually renders (matchedDoorCount), not the denormalized `chainData.count`
  // (the chain's canonical size, which can drift from the filtered rows).
  const totalCount = matchedDoorCount ?? chainData?.count ?? 0
  const unmatchedTableKey = `${unmatchedTableKeyPrefix}-${chainData?.id}`
  const tabIdPrefix = `doors-tabs-${chainData?.id ?? 'modal'}`

  return (
    <div className={`w-full ${className}`}>
      <PillTabs<'matched' | 'unmatched'>
        tabs={[
          {
            key: 'matched',
            title: `First Bite (${formatInteger(totalCount)})`,
          },
          {
            key: 'unmatched',
            title: `Unmatched (${formatInteger(unmatchedCount)})`,
          },
        ]}
        activeKey={selectedTab}
        onChange={setSelectedTab}
        idPrefix={tabIdPrefix}
        aria-label={`${chainData?.name ?? 'Chain'} doors`}
      />

      {/* Both panels stay mounted (rather than <TabPanel>, which unmounts the
          inactive panel) so the Unmatched table can report its row count into
          the tab label before that tab is ever opened. We still wire the
          tabpanel ARIA by hand and toggle visibility with Tailwind. */}
      <TableLinkStyleProvider variant="plain">
        <div
          role="tabpanel"
          id={`${tabIdPrefix}-panel-matched`}
          aria-labelledby={`${tabIdPrefix}-tab-matched`}
          tabIndex={0}
          className={`mt-4 max-h-[600px] overflow-hidden rounded-lg border border-neutral-200 ${
            selectedTab === 'matched' ? 'flex' : 'hidden'
          }`}
        >
          <MatchedTableComponent
            chainID={chainID}
            forChainProxyId={forChainProxyId}
            chainData={chainData}
            chainProxy={chainProxy}
            unmatchedTableKey={unmatchedTableKey}
            // Report the matched table's real row count up so the badge +
            // modal header reflect what's actually listed (FIR-7760). Placed
            // before the spread so a caller-supplied setDoorsSummary wins.
            setDoorsSummary={setMatchedSummary}
            tableKey={
              matchedTableKey || `${subCategory.slug}-table-${chainData?.id}`
            }
            {...matchedTableProps}
          />
        </div>

        <div
          role="tabpanel"
          id={`${tabIdPrefix}-panel-unmatched`}
          aria-labelledby={`${tabIdPrefix}-tab-unmatched`}
          tabIndex={0}
          className={`mt-4 max-h-[600px] overflow-hidden rounded-lg border border-neutral-200 ${
            selectedTab === 'unmatched' ? 'flex' : 'hidden'
          }`}
        >
          {chainProxy && (
            <UnmatchedDoorsTable
              tableKey={unmatchedTableKey}
              filterIdentifierModifier={
                chainProxy.id.toString() as FilterIdentifier
              }
              chainProxy={chainProxy}
              setTotalRowsCount={handleUnmatchedCountChange}
            />
          )}
        </div>
      </TableLinkStyleProvider>
    </div>
  )
}
